import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Phone, Envelope, MapPin, FacebookLogo, InstagramLogo, TwitterLogo, TiktokLogo, YoutubeLogo } from '@phosphor-icons/react';
import { useSiteBranding } from '../hooks/useSiteBranding';
import { telHref } from '../lib/mainSiteBranding';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Footer() {
  // Single source of truth — on the main marketing site this returns
  // hardcoded TWR defaults; on a tenant's custom domain (or ?cdp= preview)
  // it returns that tenant's POS Site Branding values.
  const { isCustomDomain, tenantName, phone, email, logoUrl, socials } = useSiteBranding();

  const [locations, setLocations] = useState([]);

  useEffect(() => {
    // Public locations — used to build the "Services by City" SEO interlink block.
    axios.get(`${API_URL}/api/locations`)
      .then(({ data }) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => setLocations([]));
  }, []);

  // De-dupe city+state combos
  const cityStateList = useMemo(() => {
    const map = new Map();
    locations.forEach((l) => {
      if (!l.city || !l.state) return;
      const key = `${l.city.toLowerCase().replace(/\s+/g, '-')}-${l.state.toLowerCase()}`;
      if (!map.has(key)) {
        map.set(key, { key, city: l.city, state: l.state.toUpperCase() });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.state === b.state ? a.city.localeCompare(b.city) : a.state.localeCompare(b.state)
    );
  }, [locations]);

  return (
    <footer className="bg-[#050505] border-t border-[#262626]" data-testid="footer">
      <div className="container-padding py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center mb-6">
              {isCustomDomain && logoUrl && logoUrl !== '/logo-tw.png' ? (
                <img src={logoUrl} alt={tenantName} className="h-16 w-auto" data-testid="footer-tenant-logo" />
              ) : isCustomDomain ? (
                <span className="text-2xl font-black text-white tracking-tight">{tenantName}</span>
              ) : (
                <img src="/logo-tw.png" alt="Total Wireless Repair Center" className="h-16 w-auto" />
              )}
            </Link>
            <p className="text-[#A3A3A3] text-sm leading-relaxed">
              Your trusted partner for all electronic device repairs. Fast, reliable, and affordable service.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wide mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Services', href: '/services' },
                { name: 'Locations', href: '/locations' },
                { name: 'Book Repair', href: '/booking' },
                { name: 'Repair Guides', href: '/guides' },
                { name: 'Get Quote', href: '/quote' },
                { name: 'Track Repair', href: '/track' },
              ].map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors text-sm">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wide mb-6">Our Services</h4>
            <ul className="space-y-3">
              {[
                { name: 'Phone Repair', slug: 'phone-repair' },
                { name: 'iPhone Repair', slug: 'iphone-repair' },
                { name: 'Tablet & iPad Repair', slug: 'tablet-repair' },
                { name: 'Computer Repair', slug: 'computer-repair' },
                { name: 'Game Console Repair', slug: 'game-console-repair' },
              ].map((service) => {
                // Deep-link to the first city's service page if we know any city; otherwise fall back to /services.
                const first = cityStateList[0];
                const href = first ? `/${service.slug}/${first.key}` : '/services';
                return (
                  <li key={service.name}>
                    <Link to={href} className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors text-sm" data-testid={`footer-service-${service.slug}`}>
                      {service.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wide mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-[#A3A3A3] text-sm">
                <Phone size={18} className="text-[#E3000F]" weight="bold" />
                <a href={telHref(phone)} className="hover:text-white" data-testid="footer-phone">
                  {phone}
                </a>
              </li>
              <li className="flex items-center gap-3 text-[#A3A3A3] text-sm">
                <Envelope size={18} className="text-[#E3000F]" weight="bold" />
                <a href={`mailto:${email}`} className="hover:text-white break-all" data-testid="footer-email">
                  {email}
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm">
                <MapPin size={18} className="text-[#E3000F] flex-shrink-0 mt-0.5" weight="bold" />
                <Link to="/contact" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" data-testid="footer-contact-link">
                  Contact Us Page
                </Link>
              </li>
            </ul>

            {/* Social */}
            <div className="flex gap-4 mt-6" data-testid="footer-socials">
              {socials.facebook && (
                <a href={socials.facebook} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" aria-label="Facebook" data-testid="footer-social-facebook">
                  <FacebookLogo size={24} weight="bold" />
                </a>
              )}
              {socials.instagram && (
                <a href={socials.instagram} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" aria-label="Instagram" data-testid="footer-social-instagram">
                  <InstagramLogo size={24} weight="bold" />
                </a>
              )}
              {socials.twitter && (
                <a href={socials.twitter} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" aria-label="Twitter" data-testid="footer-social-twitter">
                  <TwitterLogo size={24} weight="bold" />
                </a>
              )}
              {socials.tiktok && (
                <a href={socials.tiktok} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" aria-label="TikTok" data-testid="footer-social-tiktok">
                  <TiktokLogo size={24} weight="bold" />
                </a>
              )}
              {socials.youtube && (
                <a href={socials.youtube} target="_blank" rel="noreferrer" className="text-[#A3A3A3] hover:text-[#E3000F] transition-colors" aria-label="YouTube" data-testid="footer-social-youtube">
                  <YoutubeLogo size={24} weight="bold" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-[#262626] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#A3A3A3] text-sm" data-testid="footer-copyright">
            {isCustomDomain
              ? `${tenantName} ${new Date().getFullYear()}`
              : `${tenantName}, an Anything Electronics company. © ${new Date().getFullYear()}`}
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-[#A3A3A3] hover:text-white text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-[#A3A3A3] hover:text-white text-sm transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
